import { exact, number, string } from "prop-types";
import { useHistory } from "react-router";
import { useContextPerson } from "hooks/persons";
import { Card, Heading, Loader, Separator } from "uikit/components";
import { toShortDateStringLocale } from "utils";
import SchoolIndicatorCard from "../SchoolIndicatorCard/SchoolIndicatorCard";
import SchoolSummaryCard from "../SchoolSummaryCard/SchoolSummaryCard";
import styles from "./SchoolSummaryView.module.styl";

export default function SchoolSummaryView({
    currentDate,
    previousWeekStartDate,
    previousWeekEndDate,
    currentWeekStartDate,
    currentWeekEndDate,
    nextWeekStartDate,
    nextWeekEndDate,
    schoolSummaryData,
    schoolId,
}) {
    const history = useHistory();

    const redirectToSchoolGroupsAttendance = function () {
        history.push(`/attendance/school/${schoolId}`);
    };

    const redirectToSchoolTrainingPlan = function () {
        history.push(`/training-plans/school/${schoolId}`);
    };

    const redirectToCoachStatistics = function () {
        history.push(`/coach-statistics/school/${schoolId}`);
    };

    const { sportOrgUrl, isLoading } = useContextPerson();
    const instructionUrl =
        "https://support.moisport.team/admin/my-company/kak-administratoru-prosmotret-atlas-sportivnoj-podgotovki";

    if (isLoading) {
        return <Loader position="margin-auto" />;
    }

    return (
        <Card borderRadius={"giant"} padding={"none"} className={styles.form}>
            <Heading size={"h2"} className={styles.heading} name="school-activity-title">
                Отчет по активности спортивной организации
            </Heading>
            <Separator margin={"none"} color="grey" />
            <div className={styles.summary}>
                <SchoolSummaryCard schoolId={schoolId} />
            </div>
            <Separator margin={"none"} color="grey" />
            <div className={styles.box}>
                <SchoolIndicatorCard
                    descriptionTitle={`выставлено отметок об отсутствии с ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )} по ${toShortDateStringLocale(previousWeekEndDate)}`}
                    numerator={schoolSummaryData.trainingSkippedCount}
                    percentage={schoolSummaryData.attendancePercent}
                    title="Посещаемость"
                    info="Количество отметок об отсутствии, выставленных за прошлую неделю."
                    footerBadValueLinkText="Журнал групп."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}/reports`}
                    footerBadValueText="Чтобы увеличить показатель проверьте "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    onClick={redirectToSchoolGroupsAttendance}
                    dataTestName="school_attendance"
                    instructionLinkDataTestName="school-attendance-instruction"
                    footerBadValueLinkUrlDataTestName="school-journal-reports"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.coachesCount}
                    descriptionTitle={`подтвержденных учетных записей на ${toShortDateStringLocale(
                        currentDate,
                    )}`}
                    descriptionText="Не подтверждено"
                    numerator={schoolSummaryData.activeCoachesCount}
                    percentage={schoolSummaryData.activeCoachesPercent}
                    title="Тренеры-преподаватели"
                    info="Количество подтвержденных учетных записей тренеров-преподавателей относительно количества созданных учетных записей тренеров-преподавателей."
                    footerBadValueLinkText="Активация."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}/persons/activation/staff`}
                    footerBadValueText="Чтобы увеличить показатель проверьте раздел "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    additionalLinkText={"Перейти к статистике тренеров-преподавателей"}
                    additionalLinkOnClick={redirectToCoachStatistics}
                    additionalLinkDataTestName={"school_statistics_coach_btn"}
                    footerBadValueLinkUrlDataTestName="activation_link_staff"
                    instructionLinkDataTestName="school-coach-instruction"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.sportsmansCount}
                    descriptionTitle={`подтвержденных учетных записей на ${toShortDateStringLocale(
                        currentDate,
                    )}`}
                    descriptionText="Не подтверждено"
                    numerator={schoolSummaryData.activeSportsmansCount}
                    percentage={schoolSummaryData.activeSportsmansPercent}
                    title="Обучающиеся"
                    info="Количество подтвержденных учетных записей обучающихся относительно количества созданных учетных записей обучающихся."
                    footerBadValueLinkText="Активация."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}/persons/activation/sportsmen`}
                    footerBadValueText="Чтобы увеличить показатель проверьте раздел "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    footerBadValueLinkUrlDataTestName="activation_link_sportsmen"
                    instructionLinkDataTestName="school-sportsmen-instruction"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.parentsCount}
                    descriptionTitle={`подтвержденных учетных записей на ${toShortDateStringLocale(
                        currentDate,
                    )}`}
                    descriptionText="Не подтверждено"
                    numerator={schoolSummaryData.activeParentsCount}
                    percentage={schoolSummaryData.activeParentsPercent}
                    title="Родители"
                    info="Количество подтвержденных учетных записей родителей относительно количества созданных учетных записей родителей.	"
                    footerBadValueLinkText="Активация."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}/persons/activation/parents`}
                    footerBadValueText="Чтобы увеличить показатель проверьте раздел "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    footerBadValueLinkUrlDataTestName="activation_link_parents"
                    instructionLinkDataTestName="school-parent-instruction"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.groupsCount}
                    descriptionTitle={`групп заполнили расписание c ${toShortDateStringLocale(
                        currentWeekStartDate,
                    )}
                по ${toShortDateStringLocale(currentWeekEndDate)}`}
                    descriptionText="Не заполнили"
                    numerator={schoolSummaryData.currentWeekGroupsWithTrainingsCount}
                    percentage={schoolSummaryData.currentWeekGroupsWithTrainingsPercent}
                    title="Расписание (текущая неделя)"
                    info="Количество групп, которые заполнили расписание на текущую неделю, относительно всех групп в организации."
                    footerBadValueLinkText="Расписание групп."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}`}
                    footerBadValueText="Чтобы увеличить показатель проверьте "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    instructionLinkDataTestName="school-schedule-current-week-instruction"
                    footerBadValueLinkUrlDataTestName="school-schedule-current-week-profile"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.groupsCount}
                    descriptionTitle={`групп заполнили расписание c ${toShortDateStringLocale(
                        nextWeekStartDate,
                    )}
                по ${toShortDateStringLocale(nextWeekEndDate)}`}
                    descriptionText="Не заполнили"
                    numerator={schoolSummaryData.nextWeekGroupsWithTrainingsCount}
                    percentage={schoolSummaryData.nextWeekGroupsWithTrainingsPercent}
                    title="Расписание (будущая неделя)"
                    info="Количество групп, которые заполнили расписание на будущую неделю, относительно всех групп в организации."
                    footerBadValueLinkText="Расписание групп."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}`}
                    footerBadValueText="Чтобы увеличить показатель проверьте "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    instructionLinkDataTestName="school-schedule-next-week-instruction"
                    footerBadValueLinkUrlDataTestName="school-schedule-next-week-profile"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.previousWeekTrainingCount}
                    descriptionTitle={`тренировок с отметками с ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )}
                по ${toShortDateStringLocale(previousWeekEndDate)}`}
                    descriptionText="Без отметок"
                    numerator={schoolSummaryData.previousWeekTrainingWithMarksCount}
                    percentage={schoolSummaryData.previousWeekTrainingWithMarksPercent}
                    title="Журнал"
                    info="Количество тренировок на прошлой неделе, для которых была выставлена хотя бы одна оценка или отметка, добавлен комментарий или медиа, относительно количества всех тренировок на прошлой неделе."
                    footerBadValueLinkText="Журналы групп."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}/reports`}
                    footerBadValueText="Чтобы увеличить показатель проверьте "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    instructionLinkDataTestName="school-journal-instruction"
                    footerBadValueLinkUrlDataTestName="school-journal-reports"
                />
                <SchoolIndicatorCard
                    denominator={schoolSummaryData.trainingsCount}
                    descriptionTitle={`тренировок с упражнениями с ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )}
                по ${toShortDateStringLocale(previousWeekEndDate)}`}
                    numerator={schoolSummaryData.trainingsWithFilledPlanCount}
                    percentage={schoolSummaryData.trainingsWithFilledPlanPercentage}
                    title="Ведение планов тренировок"
                    info="Количество тренировок с упражнениями относительно общего количества тренировок."
                    footerBadValueLinkText="Выполнение плана спортивной подготовки за месяц."
                    footerBadValueLinkUrl={`${sportOrgUrl}/schools/${schoolId}/reports`}
                    footerBadValueText="Чтобы увеличить показатель проверьте "
                    footerGoodValueText="Показатель на высоком уровне. Отличная работа!"
                    instructionUrl={instructionUrl}
                    onClick={redirectToSchoolTrainingPlan}
                    dataTestName="school_training_plan"
                    instructionLinkDataTestName="school-training-plan-instruction"
                    footerBadValueLinkUrlDataTestName="school-training-plan-reports"
                />
            </div>
        </Card>
    );
}

SchoolSummaryView.propTypes = {
    schoolId: number.isRequired,
    currentDate: string.isRequired,
    previousWeekStartDate: string.isRequired,
    previousWeekEndDate: string.isRequired,
    currentWeekStartDate: string.isRequired,
    currentWeekEndDate: string.isRequired,
    nextWeekStartDate: string.isRequired,
    nextWeekEndDate: string.isRequired,
    schoolSummaryData: exact({
        coachesCount: number.isRequired,
        activeCoachesCount: number.isRequired,
        activeCoachesPercent: number.isRequired,
        sportsmansCount: number.isRequired,
        activeSportsmansCount: number.isRequired,
        activeSportsmansPercent: number.isRequired,
        parentsCount: number.isRequired,
        activeParentsCount: number.isRequired,
        activeParentsPercent: number.isRequired,
        groupsCount: number.isRequired,
        currentWeekGroupsWithTrainingsCount: number.isRequired,
        currentWeekGroupsWithTrainingsPercent: number.isRequired,
        nextWeekGroupsWithTrainingsCount: number.isRequired,
        nextWeekGroupsWithTrainingsPercent: number.isRequired,
        previousWeekTrainingWithMarksCount: number.isRequired,
        previousWeekTrainingCount: number.isRequired,
        previousWeekTrainingWithMarksPercent: number.isRequired,
        trainingSkippedCount: number.isRequired,
        attendancePercent: number.isRequired,
        trainingsCount: number.isRequired,
        trainingsWithFilledPlanCount: number.isRequired,
        trainingsWithFilledPlanPercentage: number.isRequired,
    }).isRequired,
};
